var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export class WebcamBackgroundService {
    constructor() {
        this.outputCanvas = document.createElement("canvas");
        this.outputStreams = new Set();
        this.blurRadius = 9;
        this.personThreshold = 0.5;
        this.edgeSoftness = 0.2;
        this.hasProcessedFrame = false;
        this.generation = 0;
        this.backgroundImage = null;
        this.backgroundRevision = 0;
        this.originalBackground = false;
    }
    hasOutputFrame() {
        return this.hasProcessedFrame;
    }
    setVideoStream(videoStream) {
        this.stop();
        const video = document.createElement("video");
        video.srcObject = videoStream;
        video.autoplay = true;
        video.muted = true;
        video.playsInline = true;
        this.video = video;
        this.outputCanvas.width = 320;
        this.outputCanvas.height = 240;
    }
    setBlurRadius(radius) {
        if (!Number.isFinite(radius) || radius < 0 || radius > 30) {
            throw new RangeError("Webcam blur radius must be between 0 and 30 pixels");
        }
        this.blurRadius = radius;
    }
    getCanvas() {
        return this.outputCanvas;
    }
    setSegmentation(personThreshold, edgeSoftness) {
        if (![personThreshold, edgeSoftness].every((value) => Number.isFinite(value) && value >= 0 && value <= 1)) {
            throw new RangeError("Webcam person threshold and edge softness must be between 0 and 1");
        }
        this.personThreshold = personThreshold;
        this.edgeSoftness = edgeSoftness;
    }
    setBackgroundImage(image) {
        this.originalBackground = false;
        this.backgroundImage = image;
        this.backgroundRevision++;
    }
    setOriginalBackground() {
        this.setBackgroundImage(null);
        this.originalBackground = true;
    }
    getOutputStream(frameRate = 30) {
        const stream = this.outputCanvas.captureStream(frameRate);
        this.outputStreams.add(stream);
        return stream;
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.starting !== undefined) {
                yield this.starting;
                return;
            }
            if (this.worker !== undefined)
                return;
            const video = this.video;
            if (video === undefined)
                throw new Error("Webcam blur requires a camera stream");
            const generation = this.generation;
            this.starting = new Promise((resolve, reject) => {
                this.rejectStart = reject;
                const fail = (error) => {
                    var _a;
                    if (generation !== this.generation)
                        return;
                    console.error("Webcam background worker failed:", error);
                    const wasRunning = this.hasProcessedFrame;
                    this.stop(error);
                    if (wasRunning)
                        (_a = this.onError) === null || _a === void 0 ? void 0 : _a.call(this, error);
                };
                try {
                    const ctx = this.outputCanvas.getContext("2d");
                    if (ctx === null)
                        throw new Error("Webcam blur requires a canvas 2D context");
                    const worker = new Worker(new URL("./WebcamSegmentationWorker.ts", import.meta.url), { name: "webcam-segmentation" });
                    this.worker = worker;
                    this.startTimeout = window.setTimeout(() => {
                        fail(new Error("Webcam background worker did not produce a frame within 30 seconds"));
                    }, 30000);
                    const send = (message, transfer = []) => {
                        worker.postMessage(message, transfer);
                    };
                    let sentBackgroundRevision = -1;
                    const scheduleFrame = () => {
                        send({ type: "next" });
                    };
                    const captureFrame = () => __awaiter(this, void 0, void 0, function* () {
                        if (generation !== this.generation)
                            return;
                        if (video.readyState < 2) {
                            scheduleFrame();
                            return;
                        }
                        const bitmap = yield createImageBitmap(video);
                        if (generation !== this.generation) {
                            bitmap.close();
                            return;
                        }
                        try {
                            if (sentBackgroundRevision !== this.backgroundRevision) {
                                send({ type: "background", blob: this.backgroundImage });
                                sentBackgroundRevision = this.backgroundRevision;
                            }
                            send({
                                type: "frame", bitmap, timestamp: performance.now(), radius: this.blurRadius,
                                personThreshold: this.personThreshold, edgeSoftness: this.edgeSoftness,
                                mode: this.originalBackground ? "original" : this.backgroundImage === null ? "blur" : "image"
                            }, [bitmap]);
                        }
                        catch (error) {
                            bitmap.close();
                            throw error;
                        }
                        // Only the worker's response schedules the next capture: at most one frame is in flight.
                    });
                    worker.onmessage = (event) => {
                        const message = event.data;
                        if (generation !== this.generation) {
                            if (message.type === "frame")
                                message.bitmap.close();
                            return;
                        }
                        if (message.type === "error") {
                            fail(new Error(message.message));
                        }
                        else if (message.type === "ready") {
                            scheduleFrame();
                        }
                        else if (message.type === "capture") {
                            void captureFrame().catch((error) => {
                                fail(error instanceof Error ? error : new Error(String(error)));
                            });
                        }
                        else {
                            try {
                                if (this.outputCanvas.width !== message.bitmap.width || this.outputCanvas.height !== message.bitmap.height) {
                                    this.outputCanvas.width = message.bitmap.width;
                                    this.outputCanvas.height = message.bitmap.height;
                                }
                                ctx.drawImage(message.bitmap, 0, 0);
                                for (const stream of this.outputStreams) {
                                    for (const track of stream.getVideoTracks()) {
                                        if (track.readyState === "ended") {
                                            this.outputStreams.delete(stream);
                                        }
                                        else if ("requestFrame" in track && typeof track.requestFrame === "function") {
                                            track.requestFrame();
                                        }
                                    }
                                }
                                this.hasProcessedFrame = true;
                                clearTimeout(this.startTimeout);
                                this.startTimeout = undefined;
                                this.rejectStart = undefined;
                                resolve();
                                scheduleFrame();
                            }
                            catch (error) {
                                fail(error instanceof Error ? error : new Error(String(error)));
                            }
                            finally {
                                message.bitmap.close();
                            }
                        }
                    };
                    worker.onerror = (event) => {
                        event.preventDefault();
                        fail(new Error(event.message.length > 0 ? event.message : "Webcam worker could not be loaded"));
                    };
                    worker.onmessageerror = () => {
                        fail(new Error("Webcam worker returned an unreadable message"));
                    };
                    void video.play().catch((error) => {
                        fail(error instanceof Error ? error : new Error(String(error)));
                    });
                    send({ type: "init", wasmRoot: new URL("lib/mediapipe/wasm", document.baseURI).href });
                }
                catch (error) {
                    fail(error instanceof Error ? error : new Error(String(error)));
                }
            });
            yield this.starting;
        });
    }
    stop(error = new Error("Webcam background processing was stopped")) {
        var _a, _b;
        this.generation++;
        this.hasProcessedFrame = false;
        this.outputStreams.clear();
        clearTimeout(this.startTimeout);
        this.startTimeout = undefined;
        (_a = this.worker) === null || _a === void 0 ? void 0 : _a.terminate();
        this.worker = undefined;
        (_b = this.rejectStart) === null || _b === void 0 ? void 0 : _b.call(this, error);
        this.rejectStart = undefined;
        this.starting = undefined;
        if (this.video !== undefined) {
            this.video.pause();
            this.video.srcObject = null;
            this.video = undefined;
        }
    }
}
export const webcamBackgroundService = new WebcamBackgroundService();
